import { ACETable } from '@/app/common/components/Table/ACETable'
import { useAceTable } from '@/app/common/components/Table/useAceTable'
import { currencyFormatter } from '@/lib/utils'
import { ScholarshipAwardHistoryQueries } from '@/queries/scholarship-award-history/queries'
import {
  AcademicTermProgramArray,
  ScholarshipAward,
} from '@/queries/scholarship-award-history/schema'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useQuery } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import { format, parseISO } from 'date-fns'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { AcademicTermsFilter } from './_components/academic-terms-filter'

const columnHelper = createColumnHelper<ScholarshipAward>()

const columns = [
  columnHelper.accessor('student_id', {
    header: 'Student ID',
    cell: (info) => (
      <div className="font-medium text-gray-900">{info.getValue()}</div>
    ),
  }),
  columnHelper.display({
    id: 'student_name',
    header: 'Student Name',
    cell: (info) => {
      const { student } = info.row.original
      return (
        <div className="text-gray-900">
          {student.first_name} {student.last_name}
        </div>
      )
    },
  }),
  columnHelper.accessor('sequence_number', {
    header: 'Sequence Number',
    cell: (info) => <div className="text-gray-700">{info.getValue()}</div>,
  }),
  columnHelper.display({
    id: 'program_term',
    header: 'Program Term',
    cell: (info) => (
      <div className="text-gray-700">
        {info.row.original.program_academic_term.name || 'N/A'}
      </div>
    ),
  }),
  columnHelper.display({
    id: 'program_purse_name',
    header: 'Program Purse',
    cell: (info) => (
      <div
        className="max-w-xs truncate"
        title={info.row.original.program_purse_name}
      >
        {info.row.original.program_purse_name}
      </div>
    ),
  }),
  columnHelper.accessor('total_awarded', {
    header: 'Amount Funded',
    cell: (info) => (
      <div className="font-medium text-gray-900">
        {currencyFormatter.format(info.getValue())}
      </div>
    ),
  }),
  columnHelper.accessor('last_distribution', {
    header: 'Last Distribution',
    cell: (info) => {
      const date = info.getValue()
      return (
        <div className="text-gray-700">
          {date ? format(parseISO(date), 'MM/dd/yyyy') : 'N/A'}
        </div>
      )
    },
  }),
]

export default function AwardHistoryTable({
  academicTerms,
}: {
  academicTerms: AcademicTermProgramArray
}) {
  const aceTable = useAceTable()
  const programId = useSelector(selectSelectedProgram).id

  const [selectedAcademicTermId, setSelectedAcademicTermId] = useState<number>(
    academicTerms[0].id
  )

  const { data, status } = useQuery({
    ...ScholarshipAwardHistoryQueries.fetchAwards({
      page: aceTable.currentPage,
      pageSize: aceTable.recordsPerPage,
      search: aceTable.debouncedSearch,
      academicTermId: selectedAcademicTermId,
      programId: programId,
    }),
    enabled: !!selectedAcademicTermId && !!programId,
  })

  return (
    <div className="w-full h-full">
      <ACETable<ScholarshipAward>
        data={data?.data || []}
        columns={columns}
        totalRows={data?.total || 0}
        currentPage={aceTable.currentPage}
        recordsPerPage={aceTable.recordsPerPage}
        search={aceTable.search}
        onSearchChange={aceTable.onSearchChange}
        onPageChange={aceTable.onPageChange}
        onPerPageChange={aceTable.onPerPageChange}
        onRefresh={() => {}}
        title="Scholarship Award History"
        searchPlaceholder="Search by student name, ID, sequence number, or email..."
        status={status}
        filter={
          <AcademicTermsFilter
            academicTerms={academicTerms}
            value={selectedAcademicTermId.toString()}
            onValueChange={(value: string) =>
              setSelectedAcademicTermId(Number(value))
            }
          />
        }
      />
    </div>
  )
}
